import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import { Breadcrumbs } from './Breadcrumb';
import Headline6 from './Typography/Heading/Headline6';
import Headline3 from './Typography/Heading/Headline3';
import breakpoints from './Theme/breakpoints';
import Skeleton from './SkeletonLoader/Skeleton';
import ReadMoreArrow from './ReadMoreArrow';
import { mediumRoundedCorners } from './RoundedCorners';
import useCompressOnScroll from './lib/useCompressOnScroll';
import useScrollToContent from './lib/useScrollToContent';

const JobsContainer = styled.div`
  max-width: 970px;
  z-index: 3;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 0;

  @media (min-width: ${breakpoints.lg}px) {
    padding: 24px;
  }
`;

export const JobsMain = styled.div`
  padding: 0;
  color: var(--black);
  border-bottom: 1px solid var(--grey-light);
  background-color: var(--white);
  width: 100%;
  border: 1px solid var(--grey-light);
  ${mediumRoundedCorners};
  && {
    margin-top: 0;
  }
`;

const JobsHeader = styled.div<
{
  fullHeightTitle?: boolean | null;
}
>`
  padding: 24px 0 0;
  text-align: center;

  display: flex;
  flex-direction: column;
  ${(properties) => properties.fullHeightTitle && `
    word-break: break-word;
  `}
  
  @media (min-width: ${breakpoints.sm}px) {
    min-height: auto;
  }
`;

const CompanySubheader = styled(Headline6)<{
  fullHeightTitle?: boolean | null;
}
>`
  margin: 0;
  ${(properties) => properties.fullHeightTitle && `
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: calc(50vh - 190px);
`}
  @media (min-width: ${breakpoints.sm}px) {
    min-height: auto;
  }
`;

const TitleSubheader = styled.div<{
  fullHeightTitle?: boolean | null;
  scrollY: number;
}
>`
  margin: 18px 0px 36px;
  ${(properties) => properties.fullHeightTitle && `
    min-height: calc(50vh - ${
  properties.scrollY}px);
  `}
  @media (min-width: ${breakpoints.sm}px) {
    min-height: auto;
  }
`;

const ScrollToAnchor = styled.div`
  height: 0;
  position: relative;
  top: calc(60vh - 104px);
`;

const JobsFooter = styled.div`
  font-family: var(--font-secondary);
  font-size: var(--size-body1);
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  padding: 24px;
  flex-direction: column-reverse;

  @media(min-width: ${breakpoints.sm}px) {
    justify-content: space-between;
    flex-direction: row;
  }

  @media (min-width: ${breakpoints.lg}px) {
    padding: 0;
    margin-top: 16px;
  }
`;

const JobBreadcrumbs = styled(Breadcrumbs)<
{
  fullHeightTitle?: boolean | null;
}
>`
  &&& {
    ${(properties) => properties.fullHeightTitle && `
      display: none;
    `}

    @media (min-width: ${breakpoints.sm}px) {
      display: block;
      height: 24px;
    }

    :before {
      display: none;
    }
  }
`;

const JobsBackground = styled.div`
  background-color: #f8f8f8;
  width: 100vw;
  min-height: 100vh;
  position: fixed;
  z-index: -1;
  top: 0;
  left: 0;
`;

const BackToList = styled.span`
  font-family: var(--font-secondary);
  letter-spacing: 0.083rem;
  font-size: var(--size-sub1);
  text-transform: uppercase;
  display: inline-block;
  height: 24px;

  a {
    display: inline-flex;

    svg {
      padding-right: 16px;
      margin-top: -2px;
    }
  }
`;

const JobShareButtonContainer = styled.div`
  height: 40px;
  margin-bottom: 24px;

  @media(min-width: ${breakpoints.sm}px) {
    margin-bottom: 8px;
  }
`;

const JobHeaderContainer = styled.div`
  padding: 24px 24px 0;

  @media (min-width: ${breakpoints.lg}px) {
    padding: 0;
  }
`;

const jobPropertyTypes = {
  title: PropTypes.string,
  companyName: PropTypes.string,
  children: PropTypes.node,
  backButton: PropTypes.node,
  shareButtons: PropTypes.node,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.node,
  ).isRequired,
  fullHeightTitle: PropTypes.bool,
  withBackground: PropTypes.bool,
  showLoader: PropTypes.bool,
};

type JobProperties = InferProps<
  typeof jobPropertyTypes
>;

const Job = ({
  title,
  companyName,
  children,
  backButton,
  shareButtons,
  breadcrumbs,
  fullHeightTitle,
  withBackground = true,
  showLoader,
} : JobProperties) => {
  const { articleContentReference, scrollToContent } = useScrollToContent();
  const scrollY = useCompressOnScroll();

  return (
    <>
      <JobsContainer>
        <ScrollToAnchor ref={articleContentReference} />
        <JobHeaderContainer>
          {showLoader ? (
            <Skeleton variant="rectangular" width="100%">
              <BackToList />
            </Skeleton>
          ) : (
            <BackToList>
              {backButton}
            </BackToList>
          )}
          <JobsHeader fullHeightTitle={fullHeightTitle}>
            {showLoader ? (
              <Skeleton variant="rectangular" width="100%">
                <CompanySubheader
                  color="inherit"
                  fullHeightTitle={fullHeightTitle}
                >
                  &nbsp;
                </CompanySubheader>
                <TitleSubheader
                  fullHeightTitle={fullHeightTitle}
                  scrollY={scrollY}
                >
                  &nbsp;
                </TitleSubheader>
              </Skeleton>
            ) : (
              <>
                <CompanySubheader
                  color="inherit"
                  fullHeightTitle={fullHeightTitle}
                >
                  {companyName}
                </CompanySubheader>
                <TitleSubheader
                  fullHeightTitle={fullHeightTitle}
                  scrollY={scrollY}
                >
                  <Headline3 tag="h1">
                    {title}
                  </Headline3>
                  {fullHeightTitle && (
                    <ReadMoreArrow
                      onClick={scrollToContent}
                      isHidden={scrollY > 1}
                      positionStatic
                    />
                  )}
                </TitleSubheader>
              </>
            )}
          </JobsHeader>
          {showLoader ? (
            <Skeleton variant="rectangular" width="100%" />
          ) : (
            <JobBreadcrumbs
              crumbs={breadcrumbs}
              fullHeightTitle={fullHeightTitle}
            />
          )}
        </JobHeaderContainer>
        <JobsMain>
          {showLoader ? (
            <Skeleton variant="rectangular" width="100%">{children}</Skeleton>)
            : children}
        </JobsMain>
        <JobsFooter>
          {showLoader ? (
            <Skeleton variant="rectangular" width="100%">
              <BackToList />
            </Skeleton>
          ) : (
            <BackToList>
              {backButton}
            </BackToList>
          )}
          <JobShareButtonContainer>
            {showLoader ? <Skeleton variant="rectangular" width="100%" />
              : shareButtons}
          </JobShareButtonContainer>
        </JobsFooter>
      </JobsContainer>
      {withBackground && <JobsBackground />}
    </>
  );
};

Job.propTypes = jobPropertyTypes;

export default Job;
